<template>
  <WdsModal :show="show" :title="title" :z-index="4000" @cancel="handleOnCancel">
    <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>
    <slot name="initialFields"></slot>
    <slot name="commonFields">
      <WdsStack gap-sm class="m-b-15">
        <div class="text-input">
          <label>{{ `${$t('Name')}${!nameDisabled ? '*' : ''}` }}</label>
          <WdsInput
            :model-value="name"
            :placeholder="$t('Full name')"
            required
            :disabled="nameDisabled"
            :error="serverValidationErrors['name']"
            @update:model-value="updateName"
          />
        </div>
        <div class="text-input">
          <label>{{ `${$t('Email')}${!emailDisabled ? '*' : ''}` }}</label>
          <WdsInput
            :model-value="email"
            :placeholder="$t('Email address')"
            required
            :disabled="emailDisabled"
            :error="serverValidationErrors['email']"
            @update:model-value="updateEmail"
          />
        </div>
      </WdsStack>
    </slot>
    <slot name="additionalFields" />
    <template #footer>
      <WdsStack :justify-end="!helpText" :justify-space-between="!!helpText" align-center gap-lg>
        <WdsText v-if="helpText" data-test="invite-contact-help-text" secondary>
          {{ helpText }}
        </WdsText>
        <WdsStack gap-md>
          <BaseButton size="sm" @click="handleOnCancel">{{ $t('Cancel') }}</BaseButton>
          <ButtonWithSpinner
            size="sm"
            color="primary"
            :status="buttonStatus"
            :text-while-working="saveButtonTextWhileWorking"
            data-test="save-button"
            @click="handleOnConfirm"
          >
            {{ saveButtonText }}
          </ButtonWithSpinner>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup>
import { ref } from 'vue'
import { WdsAlert, WdsModal, WdsStack, WdsText, WdsInput } from '@worksome/wds-components'
import ButtonWithSpinner from '@/components/ButtonWithSpinner.vue'
import BaseButton from '@/components/BaseButton.vue'

const props = defineProps({
  /**
   * Determines whether the modal is displayed
   */
  show: {
    type: Boolean,
    default: false,
  },
  /**
   * The modal's title
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Action to perform on cancel
   */
  onCancel: {
    type: Function,
    default: null,
  },
  /**
   * Action to perform on confirm
   */
  onConfirm: {
    type: Function,
    default: null,
  },
  /**
   * Help text to display in footer
   */
  helpText: {
    type: String,
    default: '',
  },
  /**
   * Validation errors on the name or email field
   */
  serverValidationErrors: {
    type: Object,
    default: () => {},
  },
  /**
   * Contact name
   */
  name: {
    type: String,
    default: '',
  },
  /**
   * Contact email
   */
  email: {
    type: String,
    default: '',
  },
  /**
   * Overall error banner message that is not specific to a certain field
   */
  errorBannerMessage: {
    type: String,
    default: '',
  },
  /**
   * Whether the name field is disabled or not
   */
  nameDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the email field is disabled or not
   */
  emailDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Text to display on save button
   */
  saveButtonText: {
    type: String,
    default: $t('Save'),
  },
  /**
   * Text to display while working
   */
  saveButtonTextWhileWorking: {
    type: String,
    default: $t('Saving'),
  },
})

const emit = defineEmits(['update:name', 'update:email'])

const updateName = (value) => {
  emit('update:name', value)
}

const updateEmail = (value) => {
  emit('update:email', value)
}

const handleOnCancel = () => {
  props.onCancel()
}

const buttonStatus = ref('enabled')

const handleOnConfirm = async () => {
  buttonStatus.value = 'working'

  await props.onConfirm()

  buttonStatus.value = 'enabled'
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';
.text-input {
  width: 50%;
}
</style>
